import {DOCUMENT} from '@angular/common';
import {Component, Inject, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {PageTypeEnum} from '@application/helper/navigation-helper/page-type.enum';
import {RemoveConfirmationService} from '@application/helper/remove-confirmation/remove-confirmation.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {BuggyForProductionSchedule} from '@domain/production-schedule/buggy-for-production-schedule';
import {BuggyNameGenerationView} from '@domain/production-schedule/buggy-name-generation-view-type';
import {HttpBuggyCustomSettingsService} from '@infrastructure/http/buggy/http-buggy-custom-settings.service';
import {HttpBuggyService} from '@infrastructure/http/buggy/http-buggy.service';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  CanLeavePage,
  ContentSwitcherNavigationInput,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  NavigationHelperPrototypeService,
  SaveType,
  TabsMenuItem,
  TranslateService
} from '@vdw/angular-component-library';
import {Observable, Subject, takeUntil} from 'rxjs';
import {TextileService} from '../../textile-data-overview/textile.service';
import {TextileDataType} from '../../textile-data-type.enum';
import {BuggyCustomSettingsComponent} from '../custom-settings/buggy-custom-settings.component';

@Component({
  templateUrl: 'add-buggy.component.html',
  styleUrl: 'add-buggy.component.scss'
})
export class AddBuggyComponent extends BaseComponent implements OnInit, CanLeavePage {
  @Input() public pageType: PageTypeEnum;
  @Input() public itemId: number;
  @Input() public canShowHeader: boolean = false;

  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_BUGGY;
  public readonly SAVE_TYPE = SaveType;
  public readonly BUGGY_TRANSLATION_KEY = 'PLANNING.BUGGY.BUGGY';

  public addBuggyGeneralForm: FormGroup<{name: FormControl<string>}>;
  public selectedMenuItem: TabsMenuItem = {value: 0, translationKey: 'GENERAL.GENERAL'};

  protected buggyToSave: BuggyForProductionSchedule;
  protected customSettingsUpdated: Subject<void> = new Subject();
  protected buggyNameGenerationView: BuggyNameGenerationView = {
    creelName: null,
    machineName: null,
    machineQualityName: null
  };

  public constructor(
    @Inject(DOCUMENT) private document: Document,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly buggies: HttpBuggyService,
    protected readonly httpBuggyCustomSettings: HttpBuggyCustomSettingsService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly textileService: TextileService,
    private readonly translate: TranslateService,
    protected readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly removeConfirmation: RemoveConfirmationService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setGeneralFormFields();
    this.getBuggy();

    this.addBuggyGeneralForm.valueChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((_: any) => {
      this.buggyToSave = this.getCurrentBuggy();
    });
  }

  public canLeavePage(): boolean {
    return !this.addBuggyGeneralForm.dirty || this.saving;
  }

  public save(saveType: SaveType): void {
    const isValid = new FormValidationHelper().checkForms([this.addBuggyGeneralForm], this.document);
    if (isValid) {
      this.saving = true;

      const request: Observable<void | number> = this.isEditingBuggy() ? this.buggies.update(this.buggyToSave) : this.buggies.save(this.buggyToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe({
        next: (id: number) => {
          if (this.navigationHelper.contentSwitcherDialogActive()) {
            if (saveType === SaveType.SAVE_AND_CLOSE) {
              this.buggyToSave.id = id;

              this.navigationHelper.navigateBack(this.isEditingBuggy() ? [] : this.buggyToSave);
            } else if (saveType === SaveType.SAVE_AND_CREATE_NEW) {
              this.setGeneralFormFields();
            }
            return;
          }

          this.textileService.navigateAndShowToast(saveType, TextileDataType.BUGGY, this.BUGGY_TRANSLATION_KEY, this.isEditingBuggy(), this.buggyToSave.name, id);
        },
        complete: () => {
          this.finalizeSaving();
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.isEditingBuggy() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public get createdObject(): string {
    return !AssertionUtils.isNullOrUndefined(this.addBuggyGeneralForm.controls.name.value) ? this.addBuggyGeneralForm.controls.name.value : '';
  }

  public isEditingBuggy(): boolean {
    if (!AssertionUtils.isNullOrUndefined(this.pageType)) {
      return this.pageType === PageTypeEnum.EDIT;
    }

    return this.activatedRoute.snapshot.routeConfig.path === RouteUtils.paths.utilities.buggy.editBuggy.path;
  }

  public duplicateBuggy(): void {
    if (this.navigationHelper.contentSwitcherDialogActive()) {
      this.navigationHelper.navigateWithContentSwitcher({
        componentInfo: {
          component: AddBuggyComponent,
          componentInputs: {pageType: PageTypeEnum.DUPLICATE, itemId: this.itemId, canShowHeader: true}
        },
        forgetCurrentEntry: true
      });
    } else {
      this.navigationHelper.navigateWithRoute({
        route: RouteUtils.paths.utilities.buggy.duplicateBuggy.absolutePath.replace(':id', this.itemId.toString()),
        forgetCurrentEntry: true
      });
    }
  }

  public cancel(): void {
    this.navigationHelper.navigateBack(this.buggyToSave);
  }

  public deleteBuggy(): void {
    this.removeConfirmation.open(this.buggyToSave, this.buggies.delete(this.itemId), this.BUGGY_TRANSLATION_KEY, null, null, true);
  }

  public getLoadLink(): string {
    return ErrorHandlers.getLoadLink(RouteUtils.paths.utilities.buggy.editBuggy.absolutePath, this.addBuggyGeneralForm.controls.name);
  }

  public getLoadEntry(): ContentSwitcherNavigationInput {
    return {
      componentInfo: {
        component: AddBuggyComponent,
        componentInputs: {pageType: PageTypeEnum.EDIT, itemId: this.addBuggyGeneralForm?.controls?.name?.errors?.entityId, canShowHeader: true}
      }
    };
  }

  protected applyNameSuggestion(suggestion: string): void {
    this.addBuggyGeneralForm.controls.name.patchValue(suggestion);
  }

  protected async openCustomSettings(): Promise<void> {
    this.navigationHelper
      .navigateWithContentSwitcher({
        componentInfo: {
          component: BuggyCustomSettingsComponent
        }
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => this.customSettingsUpdated.next());
  }

  private setGeneralFormFields(): void {
    this.addBuggyGeneralForm = this.formBuilder.group({
      name: this.formBuilder.control(null, Validators.required, AsyncUniqueValidator.createValidator(this.buggies, null))
    });

    this.selectedMenuItem.form = this.addBuggyGeneralForm;
  }

  private setFormValues(buggy: BuggyForProductionSchedule): void {
    this.addBuggyGeneralForm.reset({
      name: this.isDuplicatingBuggy() ? null : buggy.name
    });

    if (this.isEditingBuggy()) {
      this.addBuggyGeneralForm.controls.name.setAsyncValidators([AsyncUniqueValidator.createValidator(this.buggies, buggy.name)]);
      this.addBuggyGeneralForm.controls.name.updateValueAndValidity();
    }
  }

  private getCurrentBuggy(): BuggyForProductionSchedule {
    return new BuggyForProductionSchedule(this.isEditingBuggy() ? this.itemId : null, this.addBuggyGeneralForm.value.name);
  }

  private getBuggy(): void {
    const id = this.activatedRoute.snapshot.params.id ?? this.itemId;

    if (!AssertionUtils.isNullOrUndefined(id)) {
      this.buggies
        .getById(Number(id))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((buggy: BuggyForProductionSchedule) => {
          this.itemId = buggy.id;
          this.setFormValues(buggy);
        });
    }
  }

  private isDuplicatingBuggy(): boolean {
    if (!AssertionUtils.isNullOrUndefined(this.pageType)) {
      return this.pageType === PageTypeEnum.DUPLICATE;
    }

    return this.activatedRoute.snapshot.routeConfig.path === RouteUtils.paths.utilities.buggy.duplicateBuggy.path;
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {object: this.translate.instant(this.BUGGY_TRANSLATION_KEY)}),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }
}
